import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type CSSProperties } from 'react';
import type { CustomCategory, CustomSymbol, Routine, Settings } from '@shared/schemas';
import { PIN_UNLOCK_MINUTES } from '@shared/constants';
import { BASE_CATEGORIES } from '@shared/symbols';
import { AdultGate } from '../components/AdultGate';
import { AdultPanel } from '../components/AdultPanel';
import { CategoryEditor } from '../components/CategoryEditor';
import { CategoryNav, HOME, KEYBOARD, ROUTINE } from '../components/CategoryNav';
import { ConfirmDialog } from '../components/Dialog';
import { Keyboard } from '../components/Keyboard';
import { Notice, Toast } from '../components/Notice';
import { SentenceBar } from '../components/SentenceBar';
import { SettingsDialog } from '../components/SettingsDialog';
import { SymbolEditor } from '../components/SymbolEditor';
import { RoutineEditor } from '../components/RoutineEditor';
import { RoutineView } from '../components/RoutineView';
import { SymbolTile, TileGap } from '../components/SymbolTile';
import { UpgradeDialog } from '../components/UpgradeDialog';
import { FullPageLoading } from '../components/Spinner';
import { LanguageProvider, translate, useT } from '../i18n';
import {
  displayCategories,
  displaySymbolsFor,
  findBaseSymbol,
  findDisplaySymbol,
  isBaseOverride,
  move,
  type DisplayCategory,
  type DisplaySymbol,
} from '../lib/board';
import { frequentIds, recordUse } from '../lib/frequent';
import { hiddenSets, setIn, setManyIn, withGaps } from '../lib/hidden';
import { useOnline } from '../lib/useOnline';
import { makeToken, sentenceReducer, sentenceText } from '../lib/sentence';
import { speak, speechSupported } from '../lib/speech';
import { isUnlocked, LOCKED, touch, unlock, type AdultModeState } from '../state/adultMode';
import { BoardProvider, useBoard, useBoardStore } from '../state/board';
import { useSession } from '../state/session';

/** Longest wait for fresh data before edit mode opens. */
const EDIT_REFRESH_WAIT_MS = 4000;

export function BoardPage() {
  const { user } = useSession();
  if (!user) return null;
  return (
    <BoardProvider userId={user.id}>
      <Board />
    </BoardProvider>
  );
}

type Confirm =
  | { kind: 'symbol'; symbol: CustomSymbol }
  | { kind: 'category'; category: CustomCategory; count: number }
  | { kind: 'routine'; routine: Routine };

type AdultDialog = 'gate' | 'panel' | 'settings' | 'upgrade' | null;

function Board() {
  const { user, logout } = useSession();
  const board = useBoard();
  const store = useBoardStore();
  const online = useOnline();
  const language = board.settings.language;
  const t = useCallback((key: Parameters<typeof translate>[1], vars?: Record<string, string | number>) => translate(language, key, vars), [language]);

  const [tokens, dispatch] = useReducer(sentenceReducer, []);
  const [draft, setDraft] = useState('');
  const [current, setCurrent] = useState<string>(HOME);
  // `baseId`: editing a built-in symbol (its name/picture are saved as an override with the same id).
  const [symbolEditor, setSymbolEditor] = useState<{ categoryId: string; symbol: CustomSymbol | null; baseId?: string } | null>(null);
  const [categoryEditor, setCategoryEditor] = useState<{ category: CustomCategory | null } | null>(null);
  const [routineEditor, setRoutineEditor] = useState<{ routine: Routine | null } | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [frequent, setFrequent] = useState<string[]>([]);

  // ─── Caregiver mode ─────────────────────────────────────────────────────
  const [adult, setAdult] = useState<AdultModeState>(LOCKED);
  const [dialog, setDialog] = useState<AdultDialog>(null);
  const [editing, setEditing] = useState(false);
  const unlocked = isUnlocked(adult);
  const canEdit = editing && unlocked && board.editable;

  const lock = useCallback(() => {
    setAdult(LOCKED);
    setEditing(false);
    setDialog(null);
  }, []);

  // Re-lock after PIN_UNLOCK_MINUTES without activity.
  useEffect(() => {
    if (!adult.unlockedUntil) return;
    const id = setInterval(() => {
      if (!isUnlocked(adult)) lock();
    }, 15_000);
    return () => clearInterval(id);
  }, [adult, lock]);

  const keepAlive = () => {
    if (unlocked) setAdult((s) => touch(s, PIN_UNLOCK_MINUTES));
  };

  function openAdultArea() {
    setDialog(unlocked ? 'panel' : 'gate');
  }

  // ─── Board data ─────────────────────────────────────────────────────────
  const categories = useMemo(() => displayCategories(board.categories, language), [board.categories, language]);
  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const currentCategory = categoryById.get(current);
  const hidden = useMemo(() => hiddenSets(board.hidden), [board.hidden]);

  // Stripe checkout returns to /?payment=success|cancelled
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment) {
      if (payment === 'success') setToast(t('paymentSuccess'));
      params.delete('payment');
      const query = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : ''));
    }
  }, [t]);

  // A category deleted on another device (or hidden, once editing ends) is left.
  useEffect(() => {
    if (current === HOME || current === KEYBOARD) return;
    if (current === ROUTINE) {
      if (!canEdit && board.routines.length === 0) setCurrent(HOME);
      return;
    }
    if (!categoryById.has(current) || (!canEdit && hidden.categories.has(current))) setCurrent(HOME);
  }, [current, categoryById, canEdit, hidden, board.routines.length]);

  const pressSymbol = useCallback(
    (symbol: DisplaySymbol) => {
      dispatch({ type: 'add', token: makeToken(symbol.label, symbol.picto) });
      const { settings, usage } = store.getState();
      // Each word is spoken as it is pressed; "Falar" then reads the whole sentence.
      if (settings.speakOnTap && user!.canUseVoice) speak(symbol.label, settings);
      store.update('usage', recordUse(usage, symbol.id));
    },
    [store, user],
  );

  // "Frequentes" is worked out when the screen opens (and when data first loads), not on
  // every press: buttons must not move while the child is using them.
  const latest = useRef({ board, categoryById, language, hidden });
  latest.current = { board, categoryById, language, hidden };
  useEffect(() => {
    if (current !== HOME || !board.loaded) return;
    const { board: b, categoryById: cats, language: lang, hidden: h } = latest.current;
    // Hidden symbols (and symbols of hidden categories) never show up in Frequentes.
    const exists = (id: string) => {
      const s = findDisplaySymbol(id, b.symbols, lang);
      return !!s && cats.has(s.categoryId) && !h.symbols.has(id) && !h.categories.has(s.categoryId);
    };
    setFrequent(frequentIds(b.usage, exists));
  }, [current, board.loaded]);

  /** Routine steps: spoken when the voice is available, silently skipped otherwise. */
  function speakWord(text: string) {
    if (user!.canUseVoice && speechSupported()) speak(text, board.settings);
  }

  function setHidden(kind: 'symbols' | 'categories', ids: string[], hide: boolean) {
    const list = ids.length === 1 ? setIn(board.hidden[kind], ids[0]!, hide) : setManyIn(board.hidden[kind], ids, hide);
    if (list !== board.hidden[kind]) store.update('hidden', { ...board.hidden, [kind]: list });
  }

  function saveRoutine(routine: Routine) {
    const exists = board.routines.some((r) => r.id === routine.id);
    store.update('routines', exists ? board.routines.map((r) => (r.id === routine.id ? routine : r)) : [...board.routines, routine]);
    setRoutineEditor(null);
  }

  function moveRoutine(id: string, delta: -1 | 1) {
    store.update('routines', move(board.routines, board.routines.findIndex((r) => r.id === id), delta));
  }

  function say(text: string) {
    // The subscription screen is only offered in the caregiver area, not to the person communicating.
    if (!user!.canUseVoice) return setToast(t('voiceLocked'));
    if (!speechSupported()) return setToast(t('voiceUnavailable'));
    speak(text, board.settings);
  }

  function saveSymbol(symbol: CustomSymbol) {
    const base = findBaseSymbol(symbol.id);
    // A built-in symbol saved without changes (original picture and name) needs no override.
    if (base && !symbol.image && symbol.text.pt === base.text.pt && symbol.text.en === base.text.en) {
      restoreBase(symbol.id);
      return;
    }
    const exists = board.symbols.some((s) => s.id === symbol.id);
    store.update('symbols', exists ? board.symbols.map((s) => (s.id === symbol.id ? symbol : s)) : [...board.symbols, symbol]);
    setSymbolEditor(null);
  }

  function restoreBase(id: string) {
    store.update('symbols', board.symbols.filter((s) => s.id !== id));
    setSymbolEditor(null);
  }

  function editBase(id: string) {
    const base = findBaseSymbol(id)!;
    const override = board.symbols.find((s) => s.id === id);
    setSymbolEditor({
      categoryId: base.categoryId,
      baseId: id,
      symbol: override ?? { id, categoryId: base.categoryId, text: { ...base.text }, icon: base.icon, image: null },
    });
  }

  function moveSymbol(symbol: CustomSymbol, delta: -1 | 1) {
    // Overrides of built-in symbols keep their place; only personalised symbols move.
    const indexes = board.symbols.flatMap((s, i) => (s.categoryId === symbol.categoryId && !isBaseOverride(s) ? [i] : []));
    const pos = indexes.indexOf(board.symbols.indexOf(symbol));
    const target = indexes[pos + delta];
    if (target === undefined) return;
    store.update('symbols', move(board.symbols, indexes[pos]!, target - indexes[pos]!));
  }

  function saveCategory(category: CustomCategory) {
    const exists = board.categories.some((c) => c.id === category.id);
    store.update('categories', exists ? board.categories.map((c) => (c.id === category.id ? category : c)) : [...board.categories, category]);
    if (!exists) setCurrent(category.id);
    setCategoryEditor(null);
  }

  function moveCategory(id: string, delta: -1 | 1) {
    store.update('categories', move(board.categories, board.categories.findIndex((c) => c.id === id), delta));
  }

  function confirmDelete() {
    if (!confirm) return;
    if (confirm.kind === 'symbol') {
      store.update('symbols', board.symbols.filter((s) => s.id !== confirm.symbol.id));
    } else if (confirm.kind === 'routine') {
      store.update('routines', board.routines.filter((r) => r.id !== confirm.routine.id));
    } else {
      const id = confirm.category.id;
      store.updateMany({
        categories: board.categories.filter((c) => c.id !== id),
        symbols: board.symbols.filter((s) => s.categoryId !== id),
      });
      setCurrent(HOME);
    }
    setConfirm(null);
  }

  function saveSettings(settings: Settings) {
    store.update('settings', settings);
    setDialog('panel');
  }

  if (!board.loaded) return <FullPageLoading label={t('loading')} />;

  const cols = board.settings.gridSize;
  const takenNames = [
    ...BASE_CATEGORIES.flatMap((c) => [c.name.pt.toLowerCase(), c.name.en.toLowerCase()]),
    ...board.categories.map((c) => c.name.toLowerCase()),
  ];

  return (
    <LanguageProvider language={language}>
      <div className="board" data-cols={cols} style={{ '--cols': cols } as CSSProperties} onPointerDown={keepAlive}>
        {!online && <Notice kind="warning">{t('offline')}</Notice>}
        {online && board.syncError && <Notice kind="warning">{t('syncError')}</Notice>}
        {canEdit && (
          <div className="edit-banner" role="status">
            <span>
              ✏️ <strong>{t('editingBanner')}</strong>
              {current === HOME || current === KEYBOARD ? ` · ${t('editingHomeHint')}` : ''}
            </span>
            <button type="button" className="btn btn--primary btn--small" onClick={lock}>
              ✓ {t('done')}
            </button>
          </div>
        )}

        <SentenceBar
          tokens={tokens}
          draft={current === KEYBOARD ? draft : ''}
          onSpeak={() => say(sentenceText(tokens, current === KEYBOARD ? draft : ''))}
          onDeleteWord={() => dispatch({ type: 'removeLast' })}
          onClearAll={() => {
            dispatch({ type: 'clear' });
            setDraft('');
          }}
          onRemoveToken={(index) => dispatch({ type: 'removeAt', index })}
          onOpenSettings={openAdultArea}
        />

        <CategoryNav
          categories={categories}
          current={current}
          onSelect={setCurrent}
          onNewCategory={() => setCategoryEditor({ category: null })}
          canEdit={canEdit}
          hiddenCategories={hidden.categories}
          showRoutine={canEdit || board.routines.length > 0}
        />

        <main className="board__content" id="conteudo">
          {current === HOME && (
            <FrequentView ids={frequent} categories={categoryById} customSymbols={board.symbols} onPress={pressSymbol} />
          )}

          {current === ROUTINE && (
            <RoutineView
              userId={user!.id}
              routines={board.routines}
              find={(id) => {
                const symbol = findDisplaySymbol(id, board.symbols, language);
                const category = symbol && categoryById.get(symbol.categoryId);
                return symbol && category ? { symbol, color: category.color } : null;
              }}
              canEdit={canEdit}
              onSpeak={speakWord}
              onNew={() => setRoutineEditor({ routine: null })}
              onEdit={(routine) => setRoutineEditor({ routine })}
              onDelete={(routine) => setConfirm({ kind: 'routine', routine })}
              onMove={moveRoutine}
            />
          )}

          {current === KEYBOARD && (
            <Keyboard
              value={draft}
              onChange={setDraft}
              onAddToSentence={() => {
                dispatch({ type: 'addWords', text: draft });
                setDraft('');
              }}
              onSpeak={() => say(draft)}
            />
          )}

          {currentCategory && (
            <CategoryView
              category={currentCategory}
              customCategory={board.categories.find((c) => c.id === currentCategory.id) ?? null}
              customSymbols={board.symbols}
              canEdit={canEdit}
              hiddenSymbols={hidden.symbols}
              categoryHidden={hidden.categories.has(currentCategory.id)}
              onSetHidden={(ids, hide) => setHidden('symbols', ids, hide)}
              onSetCategoryHidden={(hide) => setHidden('categories', [currentCategory.id], hide)}
              onPress={pressSymbol}
              onAdd={() => setSymbolEditor({ categoryId: currentCategory.id, symbol: null })}
              onEditSymbol={(symbol) => setSymbolEditor({ categoryId: symbol.categoryId, symbol })}
              onEditBase={editBase}
              onDeleteSymbol={(symbol) => setConfirm({ kind: 'symbol', symbol })}
              onMoveSymbol={moveSymbol}
              onEditCategory={(category) => setCategoryEditor({ category })}
              onDeleteCategory={(category) =>
                setConfirm({ kind: 'category', category, count: board.symbols.filter((s) => s.categoryId === category.id).length })
              }
              onMoveCategory={moveCategory}
            />
          )}
        </main>

        <AdultGate
          open={dialog === 'gate'}
          onClose={() => setDialog(null)}
          onUnlocked={() => {
            setAdult(unlock(PIN_UNLOCK_MINUTES));
            setDialog('panel');
          }}
        />
        <AdultPanel
          open={dialog === 'panel' && unlocked}
          onClose={() => setDialog(null)}
          onEdit={() => {
            setDialog(null);
            // Bring in changes from other devices first, so an outdated device does not
            // overwrite them. Offline (or slow), editing starts anyway after a short wait.
            void Promise.race([store.refresh(), new Promise((r) => setTimeout(r, EDIT_REFRESH_WAIT_MS))]).then(() =>
              setEditing(true),
            );
          }}
          onSettings={() => setDialog('settings')}
          onSubscribe={() => setDialog('upgrade')}
          onLock={lock}
          onLogout={() => void logout()}
        />
        <SettingsDialog open={dialog === 'settings' && unlocked} settings={board.settings} onClose={() => setDialog('panel')} onSave={saveSettings} />
        <UpgradeDialog open={dialog === 'upgrade' && unlocked} trialEnded={!user!.canUseVoice} onClose={() => setDialog('panel')} />
        <SymbolEditor
          open={!!symbolEditor && canEdit}
          categoryId={symbolEditor?.categoryId ?? ''}
          categoryName={categoryById.get(symbolEditor?.categoryId ?? '')?.name ?? ''}
          symbol={symbolEditor?.symbol ?? null}
          onClose={() => setSymbolEditor(null)}
          onSave={saveSymbol}
          original={(() => {
            const base = symbolEditor?.baseId ? findBaseSymbol(symbolEditor.baseId) : undefined;
            return base ? { text: base.text, icon: base.icon, image: base.image } : undefined;
          })()}
          onRestore={
            symbolEditor?.baseId && board.symbols.some((s) => s.id === symbolEditor.baseId)
              ? () => restoreBase(symbolEditor.baseId!)
              : undefined
          }
        />
        <CategoryEditor
          open={!!categoryEditor && canEdit}
          category={categoryEditor?.category ?? null}
          takenNames={takenNames}
          onClose={() => setCategoryEditor(null)}
          onSave={saveCategory}
        />
        <RoutineEditor
          open={!!routineEditor && canEdit}
          routine={routineEditor?.routine ?? null}
          categories={categories}
          customSymbols={board.symbols}
          onClose={() => setRoutineEditor(null)}
          onSave={saveRoutine}
        />
        <ConfirmDialog
          open={!!confirm && canEdit}
          title={t('delete')}
          message={
            confirm?.kind === 'symbol'
              ? t('confirmDeleteSymbol', { word: confirm.symbol.text[language] })
              : confirm?.kind === 'routine'
                ? t('confirmDeleteRoutine', { name: confirm.routine.name })
              : confirm?.kind === 'category'
                ? confirm.count
                  ? t('confirmDeleteCategoryWithSymbols', { name: confirm.category.name, count: confirm.count })
                  : t('confirmDeleteCategory', { name: confirm.category.name })
                : ''
          }
          confirmLabel={t('delete')}
          danger
          onConfirm={confirmDelete}
          onCancel={() => setConfirm(null)}
        />
        <Toast message={toast} onDone={() => setToast(null)} />
      </div>
    </LanguageProvider>
  );
}

// ─── Frequentes ───────────────────────────────────────────────────────────

/** The symbols this person uses most, on one simple screen (see lib/frequent.ts). */
function FrequentView({
  ids,
  categories,
  customSymbols,
  onPress,
}: {
  ids: string[];
  categories: Map<string, DisplayCategory>;
  customSymbols: CustomSymbol[];
  onPress(symbol: DisplaySymbol): void;
}) {
  const t = useT();
  const board = useBoard();
  const symbols = ids
    .map((id) => findDisplaySymbol(id, customSymbols, board.settings.language))
    .filter((s): s is DisplaySymbol => !!s && categories.has(s.categoryId));

  return (
    <section className="category" aria-labelledby="frequent-title">
      <h1 id="frequent-title" className="sr-only">
        {t('frequent')}
      </h1>
      <div className="grid">
        {symbols.map((s) => (
          <SymbolTile key={s.id} symbol={s} color={categories.get(s.categoryId)!.color} onPress={onPress} />
        ))}
      </div>
    </section>
  );
}

// ─── Category ─────────────────────────────────────────────────────────────

interface CategoryViewProps {
  category: DisplayCategory;
  customCategory: CustomCategory | null;
  customSymbols: CustomSymbol[];
  /** Caregiver editing mode is on. */
  canEdit: boolean;
  /** Symbols hidden from the child (a gap in child mode, faded while editing). */
  hiddenSymbols: Set<string>;
  categoryHidden: boolean;
  onSetHidden(ids: string[], hide: boolean): void;
  onSetCategoryHidden(hide: boolean): void;
  onPress(symbol: DisplaySymbol): void;
  onAdd(): void;
  onEditSymbol(symbol: CustomSymbol): void;
  /** Change a built-in symbol's name or picture (e.g. a family photo). */
  onEditBase(id: string): void;
  onDeleteSymbol(symbol: CustomSymbol): void;
  onMoveSymbol(symbol: CustomSymbol, delta: -1 | 1): void;
  onEditCategory(category: CustomCategory): void;
  onDeleteCategory(category: CustomCategory): void;
  onMoveCategory(id: string, delta: -1 | 1): void;
}

function CategoryView(props: CategoryViewProps) {
  const { category, customCategory, customSymbols, canEdit, hiddenSymbols } = props;
  const t = useT();
  const board = useBoard();
  const symbols = displaySymbolsFor(category.id, customSymbols, board.settings.language);
  const ownInCategory = customSymbols.filter((s) => s.categoryId === category.id);
  // Editing shows every symbol; the child sees gaps where symbols are hidden.
  const cells: (DisplaySymbol | null)[] = canEdit ? symbols : withGaps(symbols, hiddenSymbols);

  return (
    <section className="category" aria-labelledby="category-title" style={{ '--accent': category.color } as CSSProperties}>
      {/* The active tab already shows the name; the heading is for screen readers. */}
      <h1 id="category-title" className="sr-only">
        {category.name}
      </h1>

      {canEdit && (
        <div className="category__tools">
          <button type="button" className="btn btn--small btn--ghost" onClick={() => props.onSetCategoryHidden(!props.categoryHidden)}>
            {props.categoryHidden ? `👁️ ${t('showCategory')}` : `🙈 ${t('hideCategory')}`}
          </button>
          {symbols.some((s) => !hiddenSymbols.has(s.id)) && (
            <button type="button" className="btn btn--small btn--ghost" onClick={() => props.onSetHidden(symbols.map((s) => s.id), true)}>
              🙈 {t('hideAllSymbols')}
            </button>
          )}
          {symbols.some((s) => hiddenSymbols.has(s.id)) && (
            <button type="button" className="btn btn--small btn--ghost" onClick={() => props.onSetHidden(symbols.map((s) => s.id), false)}>
              👁️ {t('showAllSymbols')}
            </button>
          )}
        </div>
      )}
      {canEdit && props.categoryHidden && <Notice kind="info">{t('categoryHiddenNote')}</Notice>}
      {canEdit && symbols.some((s) => hiddenSymbols.has(s.id)) && <p className="muted small hidden-hint">{t('hiddenHint')}</p>}

      {canEdit && customCategory && (
        <div className="category__tools">
          <button type="button" className="icon-btn" onClick={() => props.onMoveCategory(customCategory.id, -1)} aria-label={t('moveCategoryLeft')}>
            ◀
          </button>
          <button type="button" className="icon-btn" onClick={() => props.onMoveCategory(customCategory.id, 1)} aria-label={t('moveCategoryRight')}>
            ▶
          </button>
          <button type="button" className="btn btn--small btn--ghost" onClick={() => props.onEditCategory(customCategory)}>
            ✏️ {t('editCategory')}
          </button>
          <button type="button" className="btn btn--small btn--danger-ghost" onClick={() => props.onDeleteCategory(customCategory)}>
            🗑️ {t('deleteCategory')}
          </button>
        </div>
      )}

      {cells.length === 0 && !canEdit && <p className="muted empty">{category.icon}</p>}

      <div className="grid">
        {cells.map((s, i) => {
          if (!s) return <TileGap key={`gap-${i}`} />;
          const isHidden = hiddenSymbols.has(s.id);
          const hideBtn = (
            <button
              type="button"
              className="icon-btn"
              onClick={() => props.onSetHidden([s.id], !isHidden)}
              aria-label={`${isHidden ? t('show') : t('hide')}: ${s.label}`}
              title={isHidden ? t('show') : t('hide')}
            >
              {isHidden ? '👁️' : '🙈'}
            </button>
          );
          const own = s.custom ? ownInCategory.find((c) => c.id === s.id) : undefined;
          return (
            <SymbolTile
              key={s.id}
              symbol={s}
              color={category.color}
              onPress={props.onPress}
              hidden={canEdit && isHidden}
              controls={
                canEdit && own ? (
                  <>
                    <button type="button" className="icon-btn" onClick={() => props.onMoveSymbol(own, -1)} aria-label={`${t('moveUp')}: ${s.label}`}>
                      ◀
                    </button>
                    <button type="button" className="icon-btn" onClick={() => props.onEditSymbol(own)} aria-label={`${t('edit')}: ${s.label}`}>
                      ✏️
                    </button>
                    <button type="button" className="icon-btn icon-btn--danger" onClick={() => props.onDeleteSymbol(own)} aria-label={`${t('delete')}: ${s.label}`}>
                      🗑️
                    </button>
                    <button type="button" className="icon-btn" onClick={() => props.onMoveSymbol(own, 1)} aria-label={`${t('moveDown')}: ${s.label}`}>
                      ▶
                    </button>
                    {hideBtn}
                  </>
                ) : canEdit && !s.custom ? (
                  <>
                    <button type="button" className="icon-btn" onClick={() => props.onEditBase(s.id)} aria-label={`${t('edit')}: ${s.label}`}>
                      ✏️
                    </button>
                    {hideBtn}
                  </>
                ) : undefined
              }
            />
          );
        })}
        {canEdit && (
          <button type="button" className="tile tile--add" onClick={props.onAdd}>
            <span className="picto picto--emoji" aria-hidden="true">
              ➕
            </span>
            <span className="tile__label">{t('addSymbol')}</span>
          </button>
        )}
      </div>
    </section>
  );
}
